import { type FC, type ReactElement } from 'react';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer.ts';
import { TILE_SIZES } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { Masonry } from 'dibs-elements/exports/Masonry.tsx';

import loadingShimmer from '../../styles/hpLoadingShimmer.scss';

import { MASONRY_PROPS } from './constants.ts';
import {
    buildProductTileSizeByGridSlots,
    getShimmerItemType,
    isShimmerCollectionIndex,
} from './buildProductTileSizeByIndex.ts';
import layoutStyles from './HpSharedItemsGridContent.module.css';
import styles from './HpSharedItemsGridShimmer.module.css';
import { useHomePageContext } from '../HomePageContext.tsx';

const DEFAULT_SHIMMER_ITEM_COUNT = 32;

function buildShimmerProductTileSizeByIndex({
    count,
    startIndex = 0,
    isMobile = false,
}: {
    count: number;
    startIndex?: number;
    isMobile?: boolean;
}): Map<number, (typeof TILE_SIZES)[keyof typeof TILE_SIZES]> {
    return buildProductTileSizeByGridSlots(
        Array.from({ length: count }, (_, offset) => ({
            isCollection: isShimmerCollectionIndex(startIndex + offset, { isMobile, startIndex }),
        })),
        { startIndex, isMobile }
    );
}

const ShimmerBlock: FC<{ className?: string }> = ({ className }) => (
    <div className={classnames(loadingShimmer.shimmer, className)} />
);

const EditorialCollectionShimmer: FC = () => (
    <div
        className={classnames(dibsCss.roundedXsmall, styles.collectionContainer)}
        data-tn="hp-shared-items-grid-editorial-collection-shimmer"
    >
        <ShimmerBlock className={classnames(dibsCss.wFull, dibsCss.hFull)} />
    </div>
);

const PersonalizedCollectionShimmer: FC = () => (
    <div
        className={classnames(dibsCss.wFull, styles.collectionContainer)}
        data-tn="hp-shared-items-grid-personalized-collection-shimmer"
    >
        <ShimmerBlock className={classnames(dibsCss.wFull, dibsCss.hFull)} />
    </div>
);

const HpSharedItemsGridShimmerItem: FC<{
    index: number;
    productTileSizeByIndex: Map<number, (typeof TILE_SIZES)[keyof typeof TILE_SIZES]>;
    isMobile?: boolean;
    startIndex?: number;
}> = ({ index, productTileSizeByIndex, isMobile = false, startIndex = 0 }) => {
    const type = getShimmerItemType(index, { isMobile, startIndex });

    if (type === 'editorial') {
        return <EditorialCollectionShimmer />;
    }

    if (type === 'personalized') {
        return <PersonalizedCollectionShimmer />;
    }

    const tileSize = productTileSizeByIndex.get(index) ?? TILE_SIZES.xLarge;

    return <BuyerProductTileShimmer size={tileSize} />;
};

type HpSharedItemsGridShimmerItemsProps = {
    count: number;
    startIndex?: number;
    productTileSizeByIndex?: Map<number, (typeof TILE_SIZES)[keyof typeof TILE_SIZES]>;
    isMobile?: boolean;
};

export function getHpSharedItemsGridShimmerItems({
    count,
    startIndex = 0,
    productTileSizeByIndex,
    isMobile = false,
}: HpSharedItemsGridShimmerItemsProps): ReactElement[] {
    const resolvedProductTileSizeByIndex =
        productTileSizeByIndex ??
        buildShimmerProductTileSizeByIndex({ count, startIndex, isMobile });

    return Array.from({ length: count }, (_, index) => {
        const itemIndex = startIndex + index;
        const isCollection = isShimmerCollectionIndex(itemIndex, { isMobile, startIndex });
        let itemClassName: string | undefined;

        if (isMobile) {
            itemClassName = isCollection ? layoutStyles.collectionGridItem : layoutStyles.gridItem;
        }

        return (
            <div key={itemIndex} className={itemClassName}>
                <HpSharedItemsGridShimmerItem
                    index={itemIndex}
                    productTileSizeByIndex={resolvedProductTileSizeByIndex}
                    isMobile={isMobile}
                    startIndex={startIndex}
                />
            </div>
        );
    });
}

type HpSharedItemsGridShimmerProps = {
    itemCount?: number;
};

export const HpSharedItemsGridShimmer: FC<HpSharedItemsGridShimmerProps> = ({
    itemCount = DEFAULT_SHIMMER_ITEM_COUNT,
}) => {
    const { isMobile } = useHomePageContext();
    const shimmerItems = getHpSharedItemsGridShimmerItems({ count: itemCount, isMobile });

    return (
        <section className={dibsCss.mbLarger} data-tn="hp-shared-items-grid-shimmer">
            {isMobile ? (
                <div className={layoutStyles.mobileGrid}>{shimmerItems}</div>
            ) : (
                <Masonry {...MASONRY_PROPS}>{shimmerItems}</Masonry>
            )}
        </section>
    );
};
